import {StyledPopover} from '@/components/commons/date-picker/style'
import {StyledCommandMenuFooterButton, StyledPopoverItem} from '@/components/commons/global-search/style'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {CalendarDateIcon, FolderIcon, MedicalCrossIcon, QueueListIcon} from '@/components/ui/icon/Icon'
import {Text} from '@/components/ui/text/Text'
import {usePermissions} from '@/features/auth/store/store'
import {useInfinitePatients} from '@/features/patients/services/useInfinitePatients'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {PopoverDisclosure, PopoverProvider, useComboboxContext, usePopoverStore} from '@ariakit/react'
import dayjs from 'dayjs'
import {useCallback, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'

export const GlobalSearchOtherActionsPopover = ({searchValue}: {searchValue?: string}) => {
    const [selectedItemIndex, setSelectedItemIndex] = useState(0)
    const {t} = useTranslation()
    const comboboxContext = useComboboxContext()
    const navigate = useNavigate()
    const popover = usePopoverStore()
    const permissions = usePermissions()

    const {remappedData: patients} = useInfinitePatients(
        {
            search: searchValue,
            limit: 20
        },
        {
            enabled: false
        }
    )
    const isMac = navigator.userAgent.includes('Mac')

    const handleKeyPress = useCallback(
        (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && event.key == 'k') {
                event.preventDefault()
                popover.show()
            }
        },
        [popover]
    )

    useEffect(() => {
        document.addEventListener('keydown', handleKeyPress)
        return () => document.removeEventListener('keydown', handleKeyPress)
    }, [handleKeyPress])

    const handleOpenClinicalRecord = () => {
        const activeValue = comboboxContext?.getState()?.activeValue
        if (activeValue) {
            navigate(`/patients/${activeValue}/medical-record`)
            comboboxContext?.hide()
        } else {
            toast.error(t('global_search:select_patient_hint'))
        }
    }

    const handleOpenLastRequest = () => {
        const activeValue = comboboxContext?.getState()?.activeValue
        const patient = patients?.find(patient => patient.id == activeValue)
        if (patient?.treatmentRequestId) {
            navigate(`/treatment-requests/${patient.treatmentRequestId}`)
            comboboxContext?.hide()
        } else {
            toast.error(t('global_search:no_treatment_request_found'))
        }
    }

    const handleOpenLastProject = () => {
        const activeValue = comboboxContext?.getState()?.activeValue
        const patient = patients?.find(patient => patient.id == activeValue)
        if (patient?.project?.id) {
            navigate(`/projects/${patient.project.id}`)
            comboboxContext?.hide()
        } else {
            toast.error(t('global_search:no_project_found'))
        }
    }

    const handleOpenInAgenda = () => {
        const activeValue = comboboxContext?.getState()?.activeValue
        if (activeValue) {
            navigate(`/agenda/patients/week/${dayjs().format('DD-MM-YYYY')}?category=custom&subjectIds=${activeValue}`)
            comboboxContext?.hide()
        } else {
            toast.error(t('global_search:select_patient_hint'))
        }
    }

    const handleOpenTreatmentRequests = () => {
        const activeValue = comboboxContext?.getState()?.activeValue
        const patient = patients?.find(patient => patient.id == activeValue)
        if (activeValue && patient?.fullName) {
            navigate(`/treatment-requests?search=${encodeURIComponent(patient.fullName)}`)
            comboboxContext?.hide()
        } else {
            toast.error(t('global_search:select_patient_hint'))
        }
    }

    const handleOpenRehabilitationProject = () => {
        const activeValue = comboboxContext?.getState()?.activeValue
        const patient = patients?.find(patient => patient.id == activeValue)
        if (activeValue && patient?.fullName) {
            navigate(`/projects?search=${encodeURIComponent(patient.fullName.trim())}`)
            comboboxContext?.hide()
        } else {
            toast.error(t('global_search:select_patient_hint'))
        }
    }

    const options = {
        first_group: [
            ...(permissions?.medicalRecords.read
                ? [
                      {
                          label: t('global_search:medical_record'),
                          onClick: handleOpenClinicalRecord,
                          icon: <MedicalCrossIcon />
                      }
                  ]
                : []),
            ...(permissions?.requests.read
                ? [
                      {
                          label: t('global_search:last_request'),
                          onClick: handleOpenLastRequest,
                          icon: <QueueListIcon />
                      }
                  ]
                : []),
            ...(permissions?.projects.read
                ? [
                      {
                          label: t('global_search:last_project'),
                          onClick: handleOpenLastProject,
                          icon: <FolderIcon />
                      }
                  ]
                : [])
        ],
        second_group: [
            ...(permissions?.agenda.read
                ? [
                      {
                          label: t('agenda:title'),
                          onClick: handleOpenInAgenda,
                          icon: <CalendarDateIcon />
                      }
                  ]
                : []),
            {
                label: t('commons:treatment_requests'),
                onClick: handleOpenTreatmentRequests,
                icon: <QueueListIcon />
            },
            {
                label: t('commons:rehabilitation_project', {count: 2}),
                onClick: handleOpenRehabilitationProject,
                icon: <FolderIcon />
            }
        ]
    }

    const allOptions = [...options.first_group, ...options.second_group]
    const totalOptions = allOptions.length

    const handlePopoverKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (!popover.getState().open) return
            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault()
                    setSelectedItemIndex(prev => (prev + 1) % totalOptions)
                    break
                case 'ArrowUp':
                    event.preventDefault()
                    setSelectedItemIndex(prev => (prev - 1 + totalOptions) % totalOptions)
                    break
                case 'Enter': {
                    event.preventDefault()
                    const selectedOption = allOptions[selectedItemIndex]
                    if (selectedOption) {
                        selectedOption.onClick()
                    }
                    break
                }
                case 'Escape':
                    event.preventDefault()
                    popover.hide()
                    break
            }
        },
        [popover, selectedItemIndex, totalOptions, allOptions]
    )

    useEffect(() => {
        if (popover.getState().open) {
            setSelectedItemIndex(0)
        }
    }, [popover])

    useEffect(() => {
        document.addEventListener('keydown', handlePopoverKeyDown)
        return () => document.removeEventListener('keydown', handlePopoverKeyDown)
    }, [popover, handlePopoverKeyDown])

    return (
        <PopoverProvider store={popover} placement="top-end">
            <PopoverDisclosure
                render={
                    <StyledCommandMenuFooterButton variant="ghost" shape="rectangle">
                        {t('global_search:other_actions')}
                        <Flexbox gap={1} align="center">
                            <span>{isMac ? 'CMD' : 'CTRL'}</span>
                            <span>K</span>
                        </Flexbox>
                    </StyledCommandMenuFooterButton>
                }
            />
            <StyledPopover gutter={8} unmountOnHide>
                <div style={{padding: '8px'}}>
                    <Flexbox direction="column" gap={2}>
                        <Flexbox direction="column">
                            <Text size="textSm" shade="500" palette="neutral" style={{paddingLeft: '6px'}}>
                                {t('global_search:open')}
                            </Text>
                            {options.first_group.map((option, index) => (
                                <StyledPopoverItem
                                    key={index}
                                    onClick={option.onClick}
                                    $isSelected={selectedItemIndex === index}
                                >
                                    {option.icon}
                                    {option.label}
                                </StyledPopoverItem>
                            ))}
                        </Flexbox>
                        <Flexbox direction="column" gap={1}>
                            <Text size="textSm" shade="500" palette="neutral" style={{paddingLeft: '6px'}}>
                                {t('global_search:open_in_action')}
                            </Text>
                            {options.second_group.map((option, index) => (
                                <StyledPopoverItem
                                    key={index}
                                    onClick={option.onClick}
                                    $isSelected={selectedItemIndex === index + options.first_group.length}
                                >
                                    {option.icon}
                                    {option.label}
                                </StyledPopoverItem>
                            ))}
                        </Flexbox>
                    </Flexbox>
                </div>
            </StyledPopover>
        </PopoverProvider>
    )
}
